/**
 * Компонент уведомления (Toast) с типами: success, error, warning, info
 * Автоматически закрывается через указанное время
 */
import { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/utils/cn';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
  testId?: string;
}

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: 'bg-shell-success-soft border-shell-success/30 text-shell-success',
  error: 'bg-shell-danger-soft border-shell-danger/30 text-shell-danger',
  warning: 'bg-shell-warning-soft border-shell-warning/30 text-shell-warning',
  info: 'bg-shell-primary-soft border-shell-primary/30 text-shell-primary-strong',
};

export function Toast({ message, type = 'info', onClose, duration = 5000, testId }: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const Icon = icons[type];

  return (
    <div
      data-testid={testId}
      className={cn(
        'flex items-center gap-3 p-4 rounded-lg border shadow-soft',
        styles[type]
      )}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={onClose}
        className="p-1 hover:bg-black/10 rounded transition-colors"
        data-testid={`${testId}-close`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

